/**
 * /dashboard/accounting/journals — Journal entries list (read surface).
 *
 * Every posted ledger movement — manual hand-keyed journals AND the
 * engine postings from invoices / bills / receipts / payments — surfaces
 * here, newest first. Journals are IMMUTABLE (Companies Act §128(1)), so
 * this is a pure read list: each row deep-links to the detail page; the
 * only write affordance is "New journal" (hand-keyed manual entry).
 *
 * Source badge maps `source_type` ("manual" / "invoice" / "bill" / ...)
 * to a muted pill so the operator can tell engine postings from manual
 * ones at a glance. The header carries NO money column (per the backend
 * journalEntryListDTO — per-entry totals live only on the detail), so
 * the list shows Entry # / Date / Narration / Source.
 *
 * Role-gate (accountingRole = company_admin | hr_manager | accountant)
 * and feature-gate (RequireFeature("accounting")) are applied by the
 * AccountingModuleShell. Nav entry: the shell's "Journals" strip item
 * points here.
 */

import { useState } from "react";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { Download, FileText, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { accountingApi, toApiError } from "@/lib/api";
import { fmtDate, formatPaise } from "@/lib/expense-format";
import { booksExportRange, istDateKey } from "@/lib/format";
import { exportToCsv } from "@/lib/csv-export";
import { useJournalEntries } from "@/hooks/use-accounting";
import { useSmPlus } from "@/lib/use-sm-plus";
import type { JournalEntryListItem } from "@/types/accounting";
import { AccountingModuleShell } from "@/components/shared/accounting-module-shell";
import { EmptyState } from "@/components/shared/empty-state";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/accounting/journals/",
)({
  component: JournalsListRoute,
});

function ListSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}

/**
 * Source-type → human label. Falls back to a title-cased token for any
 * future engine source we haven't enumerated (the column is TEXT on the
 * wire, so we never want a bare snake_case string to leak through).
 */
function sourceLabel(source: string): string {
  switch (source) {
    case "manual":
      return "Manual";
    case "invoice":
      return "Invoice";
    case "bill":
      return "Bill";
    case "receipt":
      return "Receipt";
    case "payment":
      return "Payment";
    default:
      return source
        .split("_")
        .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
        .join(" ");
  }
}

function SourceBadge({ source }: { source: string }) {
  return (
    <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
      {sourceLabel(source)}
    </span>
  );
}

/** Trigger a Blob download. Mirrors the payroll run-detail pattern. */
function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * "Export to Tally (XML)" popover (CR0.2) — date-range picker + a
 * Download button that streams the TallyPrime-importable voucher XML
 * via the authed blob path. The backend requires both dates and 422s on
 * an inverted range, so the button stays disabled until from <= to;
 * sentinel errors (invalid range, no journals) surface as toasts via
 * blobErrorToApi + toApiError.
 */
function TallyExportControl({ tenantSlug }: Readonly<{ tenantSlug: string }>) {
  const [open, setOpen] = useState(false);
  // Default (India calendar): this month from the 15th, else last month.
  // A range with nothing posted is refused by the server in plain words.
  const [from, setFrom] = useState(() => booksExportRange(istDateKey()).from);
  const [to, setTo] = useState(() => booksExportRange(istDateKey()).to);
  const [includePayroll, setIncludePayroll] = useState(false);
  const [partyLedgers, setPartyLedgers] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const valid = from !== "" && to !== "" && from <= to;
  const rangeHint =
    from === "" || to === ""
      ? "Pick both a From and a To date to download."
      : from > to
        ? "The From date must be on or before the To date."
        : null;

  async function onDownload() {
    if (!valid) return;
    setDownloading(true);
    try {
      const blob = await accountingApi.downloadTallyXML(
        from,
        to,
        includePayroll,
        partyLedgers,
      );
      downloadBlob(blob, `tally-vouchers-${from}-${to}.xml`);
      setOpen(false);
    } catch (err) {
      toast.error(toApiError(err).message);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="gap-1.5">
          <Download aria-hidden className="h-4 w-4" />
          Export to Tally (XML)
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 space-y-3"
        role="dialog"
        aria-labelledby="tally-export-title"
        aria-describedby="tally-export-desc"
      >
        <div>
          <p id="tally-export-title" className="text-sm font-medium">
            Export vouchers to Tally
          </p>
          <p id="tally-export-desc" className="mt-1 text-xs text-muted-foreground">
            Every posted journal in the range, as a TallyPrime-importable
            XML file. Ledger names come from your Tally ledger mappings.
            Cancelled documents go out as Journal vouchers that reverse them.
          </p>
          <Link
            to="/app/$tenantSlug/settings/tally-mappings"
            params={{ tenantSlug }}
            className="mt-1 inline-block text-xs font-medium text-primary underline-offset-4 hover:underline"
            data-testid="tally-export-map-ledgers"
          >
            Map ledgers to Tally names
          </Link>
        </div>
        <div className="flex items-start gap-2">
          <Checkbox
            id="tally-export-party"
            checked={partyLedgers}
            onCheckedChange={(v) => setPartyLedgers(v === true)}
            className="mt-0.5"
          />
          <div className="space-y-0.5">
            <Label htmlFor="tally-export-party" className="text-sm font-normal">
              One Ledger per Customer and Vendor
            </Label>
            <p className="text-xs text-muted-foreground">
              Receivables and payables post to each party&apos;s own ledger
              (its contact name, under Sundry Debtors / Creditors in Tally)
              instead of Trade Receivables / Trade Payables. Create those
              ledgers in Tally first.
            </p>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <Checkbox
            id="tally-export-payroll"
            checked={includePayroll}
            onCheckedChange={(v) => setIncludePayroll(v === true)}
            className="mt-0.5"
          />
          <div className="space-y-0.5">
            <Label htmlFor="tally-export-payroll" className="text-sm font-normal">
              Include Salary Journals
            </Label>
            <p className="text-xs text-muted-foreground">
              Leave this off if you import each payroll run&apos;s Tally JV —
              it carries the same salary voucher, and importing both books
              the salary twice.
            </p>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="tally-export-from">From</Label>
          <Input
            id="tally-export-from"
            type="date"
            value={from}
            max={to || undefined}
            onChange={(e) => setFrom(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="tally-export-to">To</Label>
          <Input
            id="tally-export-to"
            type="date"
            value={to}
            min={from || undefined}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>
        {rangeHint && (
          <p className="text-xs text-muted-foreground" data-testid="tally-export-hint">
            {rangeHint}
          </p>
        )}
        <Button
          className="w-full gap-1.5"
          onClick={onDownload}
          disabled={!valid || downloading}
        >
          {downloading ? (
            <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
          ) : (
            <Download aria-hidden className="h-4 w-4" />
          )}
          Download
        </Button>
      </PopoverContent>
    </Popover>
  );
}

function JournalsListRoute() {
  const { tenantSlug } = useParams({ strict: false }) as {
    tenantSlug: string;
  };
  const q = useJournalEntries();
  const items = q.data?.items ?? [];
  const isSmPlus = useSmPlus();

  function onExport() {
    exportToCsv<JournalEntryListItem>(
      "journal-entries.csv",
      items,
      [
        { key: "entry_no", label: "entry_no" },
        { key: "date", label: "date" },
        { key: "narration", label: "narration" },
        { key: "source_type", label: "source_type" },
        { key: "source", label: "document", format: (v) => (v as JournalEntryListItem["source"])?.label ?? "" },
        { key: "total_minor", label: "amount", format: (v) => ((Number(v) || 0) / 100).toFixed(2) },
      ],
    );
  }

  return (
    <AccountingModuleShell
      title="Journals"
      description="Every posted ledger entry — manual journals and engine postings. Read-only; entries are immutable (Companies Act §128(1))."
      actions={
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="gap-1.5"
            onClick={onExport}
            disabled={items.length === 0}
          >
            <Download aria-hidden className="h-4 w-4" />
            Export CSV
          </Button>
          <TallyExportControl tenantSlug={tenantSlug} />
          <Button asChild className="gap-1.5">
            <Link
              to="/app/$tenantSlug/accounting/journals/new"
              params={{ tenantSlug }}
            >
              <Plus aria-hidden className="h-4 w-4" />
              New Journal
            </Link>
          </Button>
        </div>
      }
    >
      {q.isLoading && <ListSkeleton />}

      {q.isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load journal entries</AlertTitle>
          <AlertDescription>{toApiError(q.error).message}</AlertDescription>
        </Alert>
      )}

      {q.data && items.length === 0 && (
        <EmptyState
          icon={FileText}
          title="No Journal Entries Yet"
          description="Posted journals — both hand-keyed and engine postings — will appear here."
          action={
            <Button asChild className="gap-1.5">
              <Link
                to="/app/$tenantSlug/accounting/journals/new"
                params={{ tenantSlug }}
              >
                <Plus aria-hidden className="h-4 w-4" />
                New Journal
              </Link>
            </Button>
          }
        />
      )}

      {q.data && items.length > 0 && (
        <>
        {/* Phones: one tappable row per entry — narration and amount on
            top, entry no. · date · source underneath. */}
        {!isSmPlus && (
        <ul className="divide-y rounded-md border" data-testid="journals-cards">
          {items.map((je) => (
            <li key={je.id}>
              <Link
                to="/app/$tenantSlug/accounting/journals/$id"
                params={{ tenantSlug, id: String(je.id) }}
                className="block space-y-1.5 px-3 py-3 hover:bg-muted/40"
              >
                <div className="flex items-baseline justify-between gap-3">
                  <span className="min-w-0 break-words text-sm text-gray-900">
                    {je.narration || "—"}
                  </span>
                  <span className="shrink-0 whitespace-nowrap text-sm font-semibold tabular-nums text-gray-900">
                    {typeof je.total_minor === "number" ? formatPaise(je.total_minor) : "—"}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  <span className="font-medium tabular-nums text-primary">
                    #{je.entry_no}
                  </span>
                  <span className="whitespace-nowrap">{fmtDate(je.date)}</span>
                  <SourceBadge source={je.source_type} />
                  {je.source?.number && (
                    <span className="font-mono">{je.source.number}</span>
                  )}
                </div>
              </Link>
            </li>
          ))}
        </ul>
        )}
        {isSmPlus && (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">Entry #</TableHead>
                <TableHead className="w-32">Date</TableHead>
                <TableHead>Narration</TableHead>
                <TableHead className="w-28">Source</TableHead>
                <TableHead className="w-36 text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((je) => (
                <TableRow key={je.id} className="cursor-pointer">
                  <TableCell className="font-medium tabular-nums">
                    <Link
                      to="/app/$tenantSlug/accounting/journals/$id"
                      params={{ tenantSlug, id: String(je.id) }}
                      className="block text-primary underline-offset-4 hover:underline"
                    >
                      {je.entry_no}
                    </Link>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    <Link
                      to="/app/$tenantSlug/accounting/journals/$id"
                      params={{ tenantSlug, id: String(je.id) }}
                      className="block"
                    >
                      {fmtDate(je.date)}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link
                      to="/app/$tenantSlug/accounting/journals/$id"
                      params={{ tenantSlug, id: String(je.id) }}
                      className="block text-gray-900"
                    >
                      {je.narration || "—"}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link
                      to="/app/$tenantSlug/accounting/journals/$id"
                      params={{ tenantSlug, id: String(je.id) }}
                      className="block"
                    >
                      <SourceBadge source={je.source_type} />
                      {je.source?.number && (
                        <span className="mt-0.5 block font-mono text-xs text-muted-foreground">
                          {je.source.number}
                        </span>
                      )}
                    </Link>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {typeof je.total_minor === "number" ? formatPaise(je.total_minor) : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        )}
        </>
      )}
    </AccountingModuleShell>
  );
}
